<template>
  <!-- FT-1475 round 2 (user): THE CHECKLIST ROW, BEFORE IT EXISTS — the
       storyteller's night-sheet row this character will produce, drawn in
       the Role Studio as the steps are built. Everything is read off the
       same field list the sheet reads (golem/nightInfo), and the controls
       are the sheet's own components, every one disabled: seat pickers per
       seat slot, the told control per told field, the player's own
       character pick, and the button's word (Send / Received / Done) from
       the same exchange question the sheet asks. -->
  <div class="nrp" :class="'team-' + team" aria-label="Checklist row preview">
    <div class="nrp-top">
      <span
        v-if="icon"
        class="nrp-icon"
        :style="{ backgroundImage: `url(${icon})` }"
      ></span>
      <span class="nrp-name">{{ name }}</span>
      <span class="nrp-line" v-if="line">{{ line }}</span>
    </div>
    <div class="nrp-controls">
      <span class="nrp-label" v-if="seatsWord">{{ seatsWord }}</span>
      <SeatPicker
        v-for="n in slots"
        :key="'s' + n"
        class="nrp-seat"
        :players="[]"
        disabled
        :title="seatTitle(n - 1)"
      />
      <span class="nrp-label" v-if="toldWord">{{ toldWord }}</span>
      <template v-for="(f, i) in controls">
        <span
          v-if="f.by === 'player' && kind(f) === 'character'"
          :key="'c' + i"
          class="nrp-chip nrp-theirs"
          >Their pick…</span
        >
        <OptionSelect
          v-else-if="kind(f) === 'boolean'"
          :key="'c' + i"
          class="nrp-ping"
          name="nrp-ping"
          aria-label="What you told them"
          :options="pingOptions"
          value=""
          disabled
        />
        <NumberScrub
          v-else-if="kind(f) === 'number'"
          :key="'c' + i"
          class="nrp-num"
          preset="night"
          :value="f.min || 0"
          :min="f.min || 0"
          :max="f.max || 20"
          disabled
        />
        <CharacterPicker
          v-else-if="kind(f) === 'character'"
          :key="'c' + i"
          class="nrp-char"
          :roles="[]"
          disabled
          title="What you showed them — a character"
        />
        <span v-else-if="kind(f) === 'grimoire'" :key="'c' + i" class="nrp-chip"
          >Show grimoire</span
        >
        <input
          v-else
          :key="'c' + i"
          type="text"
          class="nrp-free"
          placeholder="What you told them"
          disabled
        />
      </template>
      <span class="nrp-send" :class="'nrp-' + exchange">{{ sendWord }}</span>
    </div>
  </div>
</template>

<script>
import SeatPicker from "./SeatPicker";
import CharacterPicker from "./CharacterPicker";
import OptionSelect from "./OptionSelect";
import NumberScrub from "./NumberScrub";
import {
  FIELD_OWNERS,
  FIELD_TYPES,
  NIGHT_EXCHANGE,
  exchangeOfFields,
  renderableType,
  splitLabelsForEntry,
} from "../golem/nightInfo";

/**
 * @prop name / team / icon  the character, as the row's head shows it
 * @prop line        the row's instruction line (words → our line → reminder)
 * @prop fields      the field list the steps compose (golem/nightInfo)
 * @prop slots       seat pickers the row draws (targetCount's answer)
 * @prop label       the role's own verb, for a single-half row
 * @prop seatsLabel / toldLabel  the author's own words for the two labels
 */
export default {
  name: "NightRowPreview",
  components: { SeatPicker, CharacterPicker, OptionSelect, NumberScrub },
  props: {
    name: { type: String, default: "" },
    team: { type: String, default: "townsfolk" },
    icon: { type: String, default: "" },
    line: { type: String, default: "" },
    fields: { type: Array, default: () => [] },
    slots: { type: Number, default: 0 },
    label: { type: String, default: "" },
    seatsLabel: { type: String, default: "" },
    toldLabel: { type: String, default: "" },
  },
  data() {
    return {
      pingOptions: [
        { value: "", label: "—" },
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ],
    };
  },
  computed: {
    /** The told half — NightSheet's rowControls rule: no seat fields, and
     *  no player-filled words (those arrive as the player's own quote). */
    controls() {
      return this.fields.filter(
        (f) =>
          f.type !== FIELD_TYPES.PLAYER &&
          (f.by !== FIELD_OWNERS.PLAYER || f.type === FIELD_TYPES.CHARACTER),
      );
    },
    split() {
      return this.slots > 0 && this.controls.length > 0;
    },
    splitWords() {
      return splitLabelsForEntry({ fields: this.fields, label: this.label });
    },
    seatsWord() {
      if (this.split) return this.seatsLabel || this.splitWords.seats;
      return this.slots > 0 ? this.seatsLabel || this.label : "";
    },
    toldWord() {
      if (this.split) return this.toldLabel || this.splitWords.told;
      return this.slots > 0 ? "" : this.toldLabel || this.label;
    },
    exchange() {
      return exchangeOfFields(this.fields);
    },
    sendWord() {
      if (this.exchange === NIGHT_EXCHANGE.TELLS) return "Send";
      if (this.exchange === NIGHT_EXCHANGE.RECORDS) return "Received";
      return "Done";
    },
  },
  methods: {
    kind(f) {
      return renderableType(f.type);
    },
    /** A seat slot's hover: whose pick, and which seats it may take. */
    seatTitle(i) {
      const seat = this.fields.filter((f) => f.type === FIELD_TYPES.PLAYER)[i];
      if (!seat) return "A seat";
      const who =
        seat.by === FIELD_OWNERS.PLAYER ? "The player's own pick" : "Your pick";
      const rules = [];
      if (seat.filter === "alive") rules.push("living only");
      if (seat.filter === "dead") rules.push("dead only");
      if (seat.notSelf) rules.push("not themselves");
      return rules.length ? who + " — " + rules.join(", ") : who;
    },
  },
};
</script>

<style scoped lang="scss">
// The sheet row's own furniture, compact: the head line (icon, name, the
// instruction) over the control line. The controls are the sheet's own
// components and wear their own styles; this only lays them out.
.nrp {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 7px 8px;
  text-align: left;
  background: rgba(0, 0, 0, 0.35);
  border: 1px dashed rgba(167, 143, 205, 0.55);
  border-radius: 6px;
}
.nrp-top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.nrp-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
}
.nrp-name {
  font-weight: bold;
  font-size: 13px;
}
.nrp-line {
  flex: 1 1 100%;
  font-size: 12px;
  font-style: italic;
  opacity: 0.8;
}
.nrp-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}
.nrp-label {
  font-size: 12px;
  opacity: 0.75;
}
.nrp-seat,
.nrp-char {
  width: 104px;
}
.nrp-chip {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 8px;
  font-size: 12px;
  border: 1px solid #3d3d3d;
  border-radius: 5px;
  opacity: 0.8;
}
.nrp-theirs {
  color: #d4af55;
  border-color: rgba(212, 175, 85, 0.55);
  border-style: dashed;
  font-style: italic;
}
.nrp-free {
  flex: 1 1 120px;
  min-width: 0;
  height: 28px;
  font-size: 12px;
}
.nrp-send {
  margin-left: auto;
  padding: 3px 9px;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  border: 1px solid #3d3d3d;
  border-radius: 5px;
  opacity: 0.85;
  &.nrp-tells {
    border-color: #a78fcd;
  }
}
</style>
